import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { api } from '../../@common';

const TOKEN_KEY = 'token';

// User tier type
export type UserTier = 'tier1' | 'tier2' | 'tier3' | 'retail';

// User profile interface
export interface UserProfile {
  id: string;
  email: string;
  profile?: string;
  tier: UserTier;
  stripeCustomerId?: string;
  createdAt: Date;
  updatedAt: Date;
  archivedAt?: Date;
  physicalPerson?: {
    id: string;
    name?: string;
    avatar?: string;
    phone?: string;
    birthday?: Date;
    gender?: string;
    nationality?: string;
    profession?: string;
  };
}

export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Função para verificar se o token é válido
  const getValidToken = (): boolean => {
    const token = localStorage.getItem(TOKEN_KEY);
    return !!(token && token.trim() !== '' && token !== 'null' && token !== 'undefined');
  };

  const profile = useQuery<UserProfile>({
    queryKey: ['profile'],
    queryFn: () =>
      api
        .get('/auth/me')
        .then((res) => res.data)
        .catch((error) => {
          // Só mostra erro se não for 401 (não autorizado)
          if (error.response?.status !== 401) {
            message.error('Error loading profile');
          }
          // Remove token inválido
          localStorage.removeItem(TOKEN_KEY);
          return null;
        }),
    enabled: getValidToken(),
    retry: false, // Não tenta novamente se falhar
    refetchOnWindowFocus: false, // Não refaz a query quando a janela ganha foco
  });

  // Send access link to user's email
  const sendAccessLink = useMutation({
    mutationFn: (email: string) =>
      api.post('/auth/access-link', { email }).then((res) => res.data),
    mutationKey: ['auth', 'sendAccessLink'],
  });

  // Verify access link token when user clicks on the link in their email
  const verifyAccessLink = useMutation({
    mutationFn: async (token: string) => {
      try {
        const response = await api.post('/auth/verify-access-link', { token });
        return response.data;
      } catch (error) {
        console.error('API error in verifyAccessLink:', error);
        throw error;
      }
    },
    mutationKey: ['auth', 'verifyAccessLink'],
  });

  // Legacy 2FA authentication methods
  const authentication = useMutation({
    mutationFn: (data: { token: string; code: string }) =>
      api.post('/auth/2fa/verify', data).then((res) => res.data),
    mutationKey: ['auth', '2faVerify'],
  });

  const resend2FA = useMutation({
    mutationFn: (data: { token: string; email: string }) =>
      api.post('/auth/2fa/resend', data).then((res) => res.data),
    mutationKey: ['auth', 'resend2FA'],
  });

  const login = useMutation({
    mutationFn: (data: { email: string }) =>
      api.post('/auth/access-link', { email: data.email }).then((res) => res.data),
    mutationKey: ['auth', 'login'],
  });

  const forgotPassword = useMutation({
    mutationFn: (email: string) =>
      api.post('/auth/forgot-password', { email }).then((res) => res.data),
    mutationKey: ['auth', 'forgotPassword'],
  });

  const resetPassword = useMutation({
    mutationFn: (data: { token: string; newPassword: string }) =>
      api.post('/auth/reset-password', data).then((res) => res.data),
    mutationKey: ['auth', 'resetPassword'],
  });

  const setAuth = (token: string) => {
    window.localStorage.setItem(TOKEN_KEY, token);
  };

  const clearAuthState = () => {
    // Remove todos os possíveis tokens
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem('token');
    // Limpa todas as queries do cache
    queryClient.clear();
  };

  const logout = () => {
    clearAuthState();
    navigate('/login');
  };

  // Verify if user is authenticated
  const isAuthenticated = getValidToken();

  return {
    profile,
    authentication,
    login,
    resend2FA,
    sendAccessLink,
    verifyAccessLink,
    forgotPassword,
    resetPassword,
    setAuth,
    logout,
    clearAuthState,
    isAuthenticated,
  };
};
